import React from 'react';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { useQuery } from '@apollo/client';
import { DoneAll, Info, Lock } from '@mui/icons-material';
import { Stack, Typography, Skeleton, Button, Box } from '@mui/material';
import { UploadedFileView } from 'components/shared/UploadedFileView/UploadedFileView';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';

import {
  bpfAPI,
  StageDocumentViewModel,
  GetStageAttributeDocumentsResponse,
  GetStageAttributeDocumentsVariables,
} from '../../../api';
import { S3_BUCKET_NAME } from '../../../../../config';
import { DocumentAttributePermissions } from '../DocumentAttributePermissions';
import { StageDocumentAttributeItem, StageDocumentAttribute } from '../../../types';

interface Props {
  stageId: string;
  parentId: string;
  parentType: string;
  isLoading: boolean;
  isDeleting: boolean;
  onDelete: (id: string) => void;
  attribute: StageDocumentAttribute;
  onUpload: (files: File[], attributeItem: StageDocumentAttributeItem) => Promise<void>;
}

export const Document = ({
  stageId,
  parentId,
  onUpload,
  onDelete,
  isLoading,
  attribute,
  isDeleting,
  parentType,
}: Props) => {
  const { handleOpen: openFileUpload } = useModal(ModalsKeys.FileUploadDialog);

  const { data, error, loading, previousData } = useQuery<
    GetStageAttributeDocumentsResponse,
    GetStageAttributeDocumentsVariables
  >(bpfAPI.getStageAttributeDocuments, {
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        stageId,
        parentId,
        parentType,
      },
    },
  });

  const list = (data ?? previousData)?.bpf?.result?.list ?? [];

  const documentsMap = list.reduce<Record<string, StageDocumentViewModel>>((acc, doc) => {
    acc[doc.attributeId] = doc;
    return acc;
  }, {});

  const handleUploadClick = (item: StageDocumentAttributeItem) => {
    openFileUpload({
      filesLimit: 1,
      maxFileSize: 104857600,
      title: `Upload: ${item.name}`,
      onUpload: (files: File[]) => onUpload(files, item),
    });
  };

  return (
    <RenderWithFallback
      error={error}
      isLoading={!previousData && loading}
      fallBack={<Skeleton variant="rectangular" height={40} />}
    >
      <Stack gap="1rem" width="100%" paddingLeft="2px">
        {attribute.value.map(item => (
          <Stack key={item.id} gap="0.5rem">
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', alignItems: 'center', gap: '1rem' }}>
              <Typography noWrap>
                {item.name}

                {item.isRequired && (
                  <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>
                    *
                  </Typography>
                )}
              </Typography>

              {!item.value ? (
                <Stack alignItems="flex-end">
                  <Button variant="text" loading={isLoading} onClick={() => handleUploadClick(item)}>
                    Upload
                  </Button>
                </Stack>
              ) : (
                (documentsMap[item.id]?.documents ?? []).map(doc => (
                  <DocumentAttributePermissions
                    key={doc.id}
                    roles={documentsMap[item.id].availableForRoles}
                    fallBack={
                      <Stack direction="row" alignItems="center" justifyContent="flex-end" gap="0.5rem">
                        <Stack direction="row" alignItems="center" gap="0.2rem">
                          <Lock color="disabled" fontSize="small" />
                          <Typography color="textSecondary">Restricted</Typography>
                        </Stack>

                        <Stack direction="row" alignItems="center" gap="0.2rem">
                          {item.value ? <DoneAll color="success" /> : <Info color="error" />}
                          <Typography color={item.value ? 'success' : 'error'}>
                            {item.value ? 'Uploaded' : 'Required file missing'}
                          </Typography>
                        </Stack>
                      </Stack>
                    }
                  >
                    <UploadedFileView
                      isDeleting={isDeleting}
                      name={doc.docName ?? item.name}
                      onDelete={() => onDelete(documentsMap[item.id].id)}
                      onView={() => window.open(`/document/${doc.id}`, '_blank')}
                      onDownload={() =>
                        window.open(`https://${S3_BUCKET_NAME}.s3-us-west-2.amazonaws.com/${doc.source}`)
                      }
                    />
                  </DocumentAttributePermissions>
                ))
              )}
            </Box>
          </Stack>
        ))}
      </Stack>
    </RenderWithFallback>
  );
};
